import React, { useEffect, useRef, useState } from 'react';
import { inp, lbl, noAutofill, row2 } from './shared';

const INTERESTS = [
  'Life insurance',
  'Health Insurance',
  'Dental',
  'Vision',
  'Short-Term Medical',
  'Group Medical Insurance',
  'Aflac',
  'Cancer + Critical Illness',
  'Medicare',
  'Prescription Drug Coverage',
  'Travel Insurance',
];

const COVERAGE_FOR = ['Myself', 'Myself and my family', 'A family member', 'Other'];
const HOUSEHOLD_SIZES = Array.from({ length: 10 }, (_, index) => String(index + 1));
const FPL_2026_BASE = {
  1: 15960,
  2: 21640,
  3: 27320,
  4: 33000,
  5: 38680,
  6: 44360,
  7: 50040,
  8: 55720,
};
const FPL_ADDITIONAL_PERSON = 5680;

function fplForSize(size) {
  const count = Number(size || 0);
  if (!count) return 0;
  if (count <= 8) return FPL_2026_BASE[count];
  return FPL_2026_BASE[8] + ((count - 8) * FPL_ADDITIONAL_PERSON);
}

function money(value) {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function incomeRanges(size) {
  const fpl = fplForSize(size);
  if (!fpl) return [];
  return [
    { value: 'under-100-fpl', label: `Under 100% FPL - under ${money(fpl)}` },
    { value: '100-150-fpl', label: `100% to 150% FPL - ${money(fpl)} to ${money(fpl * 1.5)}` },
    { value: '150-200-fpl', label: `150% to 200% FPL - ${money(fpl * 1.5)} to ${money(fpl * 2)}` },
    { value: '200-250-fpl', label: `200% to 250% FPL - ${money(fpl * 2)} to ${money(fpl * 2.5)}` },
    { value: '250-400-fpl', label: `250% to 400% FPL - ${money(fpl * 2.5)} to ${money(fpl * 4)}` },
    { value: 'over-400-fpl', label: `Over 400% FPL - over ${money(fpl * 4)}` },
    { value: 'unsure', label: 'I am not sure' },
  ];
}

function toggle(list, item) {
  return list.includes(item) ? list.filter(value => value !== item) : [...list, item];
}

export default function Coverage({ data, onChange }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const selectedInterests = data.insuranceInterests || [];
  const householdSize = data.coverageFor === 'Myself' ? '1' : data.householdSize;
  const ranges = incomeRanges(householdSize);
  const showHouseholdSize = data.coverageFor && data.coverageFor !== 'Myself';

  useEffect(() => {
    function closeOnOutsideClick(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  return (
    <div>
      <div style={{ position: 'relative', marginBottom: 18 }} ref={menuRef}>
        <label style={lbl}>Insurance Interests</label>
        <button type="button" style={multiButton} onClick={() => setOpen(value => !value)}>
          <span>{selectedInterests.length ? `${selectedInterests.length} selected` : 'Select insurance interests'}</span>
          <span style={{ color: '#7a99b8' }}>▾</span>
        </button>
        {selectedInterests.length > 0 && (
          <div style={chips}>
            {selectedInterests.map(item => (
              <button key={item} type="button" style={chip} onClick={() => onChange('insuranceInterests', toggle(selectedInterests, item))}>
                {item} ×
              </button>
            ))}
          </div>
        )}
        {open && (
          <div style={menu}>
            {INTERESTS.map(item => (
              <label key={item} style={option}>
                <input
                  type="checkbox"
                  checked={selectedInterests.includes(item)}
                  onChange={() => onChange('insuranceInterests', toggle(selectedInterests, item))}
                  style={box}
                />
                {item}
              </label>
            ))}
          </div>
        )}
      </div>

      <div style={divider} />

      <div style={row2}>
        <div>
          <label style={lbl}>Do you currently have insurance?</label>
          <select style={inp} value={data.hasCurrentInsurance === null ? '' : String(data.hasCurrentInsurance)} onChange={e => onChange('hasCurrentInsurance', e.target.value === '' ? null : e.target.value === 'true')}>
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Requested Start Date</label>
          <input style={inp} type="date" value={data.requestedStartDate} onChange={e => onChange('requestedStartDate', e.target.value)} />
        </div>
      </div>

      <div style={row2}>
        <div>
          <label style={lbl}>Who is the insurance for?</label>
          <select
            style={inp}
            value={data.coverageFor}
            onChange={e => {
              const next = e.target.value;
              onChange('coverageFor', next);
              if (next === 'Myself') onChange('householdSize', '1');
              if (!next) onChange('householdIncomeRange', '');
            }}
          >
            <option value="">Select...</option>
            {COVERAGE_FOR.map(item => <option key={item}>{item}</option>)}
          </select>
        </div>
        {showHouseholdSize ? (
          <div>
            <label style={lbl}>Household Size</label>
            <select
              style={inp}
              value={data.householdSize}
              onChange={e => {
                onChange('householdSize', e.target.value);
                onChange('householdIncomeRange', '');
              }}
            >
              <option value="">Select...</option>
              {HOUSEHOLD_SIZES.map(item => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
        ) : data.coverageFor === 'Other' ? (
          <div>
          <label style={lbl}>Coverage For - Other</label>
          <input style={inp} value={data.coverageForOther} onChange={e => onChange('coverageForOther', e.target.value)} {...noAutofill('coverage-for-other')} />
          </div>
        ) : <div />}
      </div>

      {data.coverageFor === 'Other' && showHouseholdSize && (
        <div style={halfRow}>
          <div>
            <label style={lbl}>Coverage For - Other</label>
            <input style={inp} value={data.coverageForOther} onChange={e => onChange('coverageForOther', e.target.value)} {...noAutofill('coverage-for-other')} />
          </div>
        </div>
      )}

      <div style={incomeSection}>
        <p style={incomeNote}>
          Household income is optional. It helps estimate Marketplace subsidy eligibility and may affect premium assistance.
        </p>
        <div style={halfRow}>
          <div>
            <label style={lbl}>Estimated Annual Household Income Range</label>
            <select
              style={inp}
              value={data.householdIncomeRange}
              onChange={e => onChange('householdIncomeRange', e.target.value)}
              disabled={!householdSize}
            >
              <option value="">{householdSize ? 'Select range...' : 'Select household size first'}</option>
              {ranges.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </div>
        </div>
        {householdSize && (
          <p style={fplNote}>
            2026 Federal Poverty Level estimate for household size {householdSize}: {money(fplForSize(householdSize))}.
            Final eligibility is determined by the Marketplace.
          </p>
        )}
      </div>
    </div>
  );
}

const multiButton = { ...inp, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', textAlign: 'left' };
const chips = { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 };
const chip = { border: '1px solid #b8cfe8', background: '#eef6fb', color: '#1a3364', borderRadius: 999, padding: '5px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const menu = { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8, boxShadow: '0 8px 18px rgba(26,51,100,0.14)', padding: 8, marginTop: 4, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 4 };
const option = { display: 'flex', alignItems: 'center', gap: 8, padding: 8, color: '#1a3364', fontSize: 14, cursor: 'pointer' };
const box = { width: 18, height: 18, accentColor: '#1a3364' };
const divider = { borderTop: '1.5px solid #dbeef8', margin: '4px 0 18px' };
const halfRow = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };
const incomeSection = { borderTop: '1.5px solid #dbeef8', paddingTop: 18, marginTop: 4 };
const incomeNote = { margin: '0 0 12px', color: '#4a6288', fontSize: 13, lineHeight: 1.55 };
const fplNote = { margin: '10px 0 0', color: '#4a6288', fontSize: 12, lineHeight: 1.45 };
